export interface FoodCostSourceRow {
  id: string;
  last_unit_cost?: number | null;
  last_unit_cost_uom?: string | null;
  last_unit_cost_currency?: string | null;
  last_unit_cost_at?: string | null;
  last_unit_cost_document_date?: string | null;
}

export interface CostAdjustmentEventRow {
  server_rev?: number | null;
  server_ts?: string | null;
  client_ts?: string | null;
  payload?: {
    food_id?: string;
    unit_cost?: number | null;
    cost_uom?: string | null;
    currency?: string | null;
    effective_at?: string;
  };
}

export interface ProjectedFoodCostPair {
  cost: number | null;
  uom: string | null;
  currency: string | null;
  commercialDate: string;
  observedAt: string;
  serverRev: number;
}

function pairOrder(pair: ProjectedFoodCostPair): string {
  return [
    pair.commercialDate,
    pair.observedAt,
    String(pair.serverRev).padStart(20, '0'),
  ].join('|');
}

/**
 * Client read model matching refresh_food_purchase_cost_v1. Server-projected
 * foods is the base; locally pending cost_adjustment events are overlaid so an
 * offline manual correction is visible without mutating the master directly.
 */
export function projectFoodCostPairs(
  foods: readonly FoodCostSourceRow[],
  adjustments: readonly CostAdjustmentEventRow[],
): Map<string, ProjectedFoodCostPair> {
  const pairs = new Map<string, ProjectedFoodCostPair>();
  for (const food of foods) {
    const cost = food.last_unit_cost;
    const uom = food.last_unit_cost_uom?.trim() || null;
    if (
      cost === undefined
      || (
        cost !== null
        && (!Number.isFinite(cost) || cost < 0 || !uom)
      )
    ) continue;
    pairs.set(food.id, {
      cost,
      uom: cost === null ? null : uom,
      currency: cost === null
        ? null
        : food.last_unit_cost_currency?.trim().toUpperCase() || 'EUR',
      commercialDate: food.last_unit_cost_document_date
        ?? food.last_unit_cost_at?.slice(0, 10)
        ?? '',
      observedAt: food.last_unit_cost_at ?? '',
      serverRev: 0,
    });
  }

  for (const adjustment of adjustments) {
    const foodId = adjustment.payload?.food_id;
    const effectiveAt = adjustment.payload?.effective_at;
    const cost = adjustment.payload?.unit_cost;
    const uom = adjustment.payload?.cost_uom?.trim() || null;
    const currency = adjustment.payload?.currency?.trim().toUpperCase() || null;
    if (
      !foodId
      || !effectiveAt
      || (
        cost !== null
        && (
          typeof cost !== 'number'
          || !Number.isFinite(cost)
          || cost < 0
          || !uom
          || !currency
        )
      )
    ) continue;
    const candidate: ProjectedFoodCostPair = {
      cost: cost ?? null,
      uom: cost == null ? null : uom,
      currency: cost == null ? null : currency,
      observedAt: adjustment.server_ts
        ?? adjustment.client_ts
        ?? effectiveAt,
      // Match the server ranking: a manual adjustment is commercially dated
      // when it lands (client time while pending, authoritative server time
      // after sync). `effective_at` remains immutable audit context.
      commercialDate: (
        adjustment.server_ts
        ?? adjustment.client_ts
        ?? effectiveAt
      ).slice(0, 10),
      serverRev: adjustment.server_rev ?? 0,
    };
    const current = pairs.get(foodId);
    if (!current || pairOrder(candidate) >= pairOrder(current)) {
      pairs.set(foodId, candidate);
    }
  }
  return pairs;
}

export interface ReversalReferenceEvent {
  payload?: {
    reverses_event_id?: string | null;
    reverses_event_client_uuid?: string | null;
  };
}

export interface ReversibleLocalEvent {
  id?: string | null;
  client_uuid?: string | null;
}

export function reversedEventReferences(
  reverseEvents: readonly ReversalReferenceEvent[],
): Set<string> {
  return new Set(
    reverseEvents
      .flatMap((event) => [
        event.payload?.reverses_event_client_uuid,
        event.payload?.reverses_event_id,
      ])
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  );
}

export function eventIsReversed(
  event: ReversibleLocalEvent,
  reversedReferences: ReadonlySet<string>,
): boolean {
  return (
    (event.id != null && reversedReferences.has(event.id))
    || (
      event.client_uuid != null
      && reversedReferences.has(event.client_uuid)
    )
  );
}
